import React, { useMemo } from 'react';
import { useMouse } from 'ahooks';
import useAIWatermarkStore from '@/store/useAIWatermarkStore';
import useAIToolsCanvasStore from '@/store/useAIToolsCanvasStore';
import { useShallow } from 'zustand/react/shallow';
import { isPointerOverContainer } from '$/utils/pointerOverContainer';
import { CANVAS_CONTAINER_ID } from '@/components/AITools/InfiniteCanvas';
import useImageBounds from '../../../ai_eraser/hooks/useImageBounds';
import styles from '../../index.module.scss';
import { BrushManager } from '../LeftPanel';

interface BrushCursorProps {
  brushManager: BrushManager;
}

export default function BrushCursor({ brushManager }: BrushCursorProps) {
  const { currentMode } = brushManager;
  const {
    mode, singleOriginalImage, singleCompareIng, brushSize,
    singleEraserLoading, singleMediaType, singleWatermarkMode,
  } = useAIWatermarkStore(useShallow((state) => ({
    mode: state.mode,
    singleOriginalImage: state.singleOriginalImage,
    singleCompareIng: state.singleCompareIng,
    brushSize: state.brushSize,
    singleEraserLoading: state.singleEraserLoading,
    singleMediaType: state.singleMediaType,
    singleWatermarkMode: state.singleWatermarkMode,
  })));

  const { render } = useAIToolsCanvasStore(useShallow((state) => ({
    render: state.render,
  })));
  const { pageX, pageY } = useMouse();
  const imageBounds = useImageBounds();

  const showBrushCursor = mode === 'single'
    && singleMediaType === 'image'
    && singleOriginalImage
    && !singleEraserLoading
    && !singleCompareIng
    && singleWatermarkMode !== 'auto'
    && currentMode === 'smear';

  const cursorStyle = useMemo(() => {
    const {
      left, right, top, bottom,
    } = imageBounds;
    const outOfImage = pageX < left || pageX > right || pageY < top || pageY > bottom;
    const isOverCanvas = isPointerOverContainer(pageX, pageY, CANVAS_CONTAINER_ID);
    const hide = outOfImage || !isOverCanvas;
    if (render?._FC) {
      let cursor: string;
      if (hide) {
        cursor = '';
      } else {
        cursor = currentMode === 'smear' ? 'none' : 'crosshair';
      }
      render._FC.freeDrawingCursor = cursor;
    }
    return {
      display: hide ? 'none' : 'block',
      top: `${pageY - brushSize / 2}px`,
      left: `${pageX - brushSize / 2}px`,
      width: `${brushSize}px`,
      height: `${brushSize}px`,
    };
  }, [brushSize, pageX, pageY, imageBounds, render, currentMode]);

  if (!showBrushCursor) return null;

  return <div className={styles.cursor} style={cursorStyle} />;
}
